import { useQuery, useSubscription } from "urql";
import { useState, useEffect, useCallback, useMemo } from "react";
import type {
  CellContext,
  PaginationState,
  RowSelectionState,
  SortingState,
} from "@tanstack/react-table";
import { createColumnHelper } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { graphql, getFragmentData } from "../../../../__generated__/gql";
import {
  Close,
  PlayCircle,
  Search,
  Send,
  Trash,
} from "../../../../ui/Icon/Icon";
import Table from "../../../../ui/Table/Table";
import {
  ArticleStatus,
  PatternSort,
  PatternTable_PatternFragment,
  PatternsFilter,
  PatternSortField,
  SortOrder,
  TemplateNodeType,
  StringFilter,
  ArticleTypeFilter,
  DateFilter,
  ArticleStatusFilter,
  ArticleType,
} from "../../../../__generated__/gql/graphql";
import IconButton from "../../../../ui/IconButton/IconButton";
import TableCellActions from "../../../../ui/Table/TableCellActions";
import urls from "../../../../routes";
import Symbol from "../../../../ui/Symbol/Symbol";
import styles from "./PatternTable.module.scss";
import PatternTableToolbar from "./PatternTableToolbar";
import Truncate from "../../../../ui/Truncate/Truncate";
import {
  FilterGroup,
  getGraphQlDateFilter,
  getGraphQlStringFilter,
} from "../../../../ui/FilterDrawer/useFilters";
import FilterDrawer from "../../../../ui/FilterDrawer/FilterDrawer";
import FilterButton from "../../../../ui/FilterDrawer/FilterButton";
import { Filter } from "../../../../ui/FilterDrawer/types";
import FormattedDate from "../../../../ui/FormattedDate/FormattedDate";
import DeletePatternModal from "./DeletePatternModal";
import GeneratePatternModal from "./GeneratePatternModal";
import PublishPatternModal from "./PublishPatternModal";
import ToggleGroup from "../../../../ui/ToggleGroup/ToggleGroup";
import Avatar from "../../../../ui/Avatar/Avatar";
import TextInput from "../../../../ui/TextInput/TextInput";
import { useDisclosure } from "../../../../ui/util/useDisclosure";
import CancelPatternGenerationModal from "./CancelPatternGenerationModal";
import StatusBadge from "../../../../components/StatusBadge/StatusBadge";
import isSymbolColor from "../../../../ui/Symbol/is-symbol-color";
import PatternTableContext from "./PatternTableContext";

const PatternFragment = graphql(/* GraphQL */ `
  fragment PatternTable_Pattern on Pattern {
    id
    templateText
    templateNodes {
      type
      value
    }
    patternSymbols {
      id
      name
      color
      values
    }
    type
    status
    createdAt
    updatedAt
    articlesCount
    createdBy {
      id
      status
      firstName
      lastName
      email
      imageUrl
    }
    ...StatusBadge
  }
`);

const PatternTableQuery = graphql(/* GraphQL */ `
  query patterns(
    $filters: [PatternsFilter!]
    $paginationOptions: OffsetPaginationOptions
    $sort: PatternSort
  ) {
    patterns(
      filters: $filters
      paginationOptions: $paginationOptions
      sort: $sort
    ) {
      nodes {
        ...PatternTable_Pattern
      }
      totalCount
    }
  }
`);

const PatternUpdatedSubscription = graphql(/* GraphQL */ `
  subscription patternUpdated {
    patternUpdated {
      ...PatternTable_Pattern
    }
  }
`);

function PatternCell({
  row,
}: CellContext<PatternTable_PatternFragment, unknown>) {
  const [deletePatternModalOpened, deletePatternModal] = useDisclosure();
  const [generatePatternModalOpened, generatePatternModal] = useDisclosure();
  const [publishPatternModalOpened, publishPatternModal] = useDisclosure();
  const [cancelPatternGenerationModalOpened, cancelPatternGenerationModal] =
    useDisclosure();

  return (
    <>
      <Link className={styles.articleCell} to={urls.pattern(row.original.id)}>
        <Truncate className={styles.articleTitle}>
          {row.original.templateNodes?.map((templateNode, index) => {
            if (templateNode.type === TemplateNodeType.Text) {
              return <span key={index}>{templateNode.value}</span>;
            }

            const symbol = row.original.patternSymbols?.find(
              (patternSymbol) => patternSymbol.name === templateNode.value,
            );

            if (!symbol) {
              return null;
            }

            const color = symbol.color.toLowerCase();

            return (
              <Symbol
                color={isSymbolColor(color) ? color : undefined}
                count={symbol.values?.length}
                key={index}
                name={symbol.name}
                size="small"
              />
            );
          })}
        </Truncate>
        {/* <span className={styles.articleType}>
          {row.original.type ? formatArticleType(row.original.type) : "-"}
        </span> */}
      </Link>
      <TableCellActions>
        <GeneratePatternModal
          id={row.original.id}
          onClose={generatePatternModal.close}
          isOpen={generatePatternModalOpened}
        />
        <PublishPatternModal
          id={row.original.id}
          onClose={publishPatternModal.close}
          isOpen={publishPatternModalOpened}
        />
        <DeletePatternModal
          id={row.original.id}
          onClose={deletePatternModal.close}
          isOpen={deletePatternModalOpened}
        />
        <CancelPatternGenerationModal
          id={row.original.id}
          onClose={cancelPatternGenerationModal.close}
          isOpen={cancelPatternGenerationModalOpened}
        />
        {row.original.status === ArticleStatus.Processing ? (
          <IconButton
            hover="primary"
            aria-label="Cancel generation"
            IconComponent={Close}
            onClick={cancelPatternGenerationModal.open}
          />
        ) : (
          <IconButton
            hover="primary"
            aria-label="Generate"
            IconComponent={PlayCircle}
            onClick={generatePatternModal.open}
          />
        )}
        <IconButton
          hover="primary"
          aria-label="Publish"
          IconComponent={Send}
          disabled={
            row.original.status !== ArticleStatus.Generated &&
            row.original.status !== ArticleStatus.Published
          }
          onClick={publishPatternModal.open}
        />
        {/* <IconButton aria-label="Edit" iconName="edit" /> */}

        <IconButton
          hover="primary"
          aria-label="Delete"
          IconComponent={Trash}
          onClick={deletePatternModal.open}
        />
      </TableCellActions>
    </>
  );
}

function StatusCell({
  cell,
}: CellContext<PatternTable_PatternFragment, ArticleStatus>) {
  return <StatusBadge data={cell.row.original} />;
}

function UserCell({
  cell,
}: CellContext<
  PatternTable_PatternFragment,
  PatternTable_PatternFragment["createdBy"]
>) {
  const user = cell.getValue();

  if (!user || !user.firstName || !user.lastName || !user.imageUrl) {
    return null;
  }

  return (
    <div className={styles.user}>
      <Avatar
        size="xsmall"
        imageUrl={user.imageUrl}
        firstName={user.firstName}
        lastName={user.lastName}
        email={user.email}
        showName={{ firstName: true }}
      />
    </div>
  );
}
const DEFAULT_PAGINATION_STATE: PaginationState = {
  pageIndex: 0,
  pageSize: 25,
};

function PatternTable({
  status,
  deleted,
  filterGroups,
  clearFilters,
  addFilter,
  editFilter,
  deleteFilter,
  onLoad,
}: {
  status?: ArticleStatus;
  deleted?: boolean;
  filterGroups: FilterGroup[];
  clearFilters: () => void;
  addFilter: (filter: Filter, index: number) => void;
  editFilter: (filter: Filter, groupIndex: number, filterIndex: number) => void;
  deleteFilter: (groupIndex: number, filterIndex: number) => void;
  onLoad: () => void;
}) {
  useSubscription({ query: PatternUpdatedSubscription });
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [keyphrase, setKeyphrase] = useState<string>("");
  const [patternSort, setPatternSort] = useState<PatternSort>();
  const [debouncedKeyphrase, setDebouncedKeyphrase] = useState<string>("");
  const [pagination, setPagination] = useState<PaginationState>(
    DEFAULT_PAGINATION_STATE,
  );
  const [isFiltersDrawerOpen, { open, close }] = useDisclosure();
  const [selectedTableView, setSelectedTableView] = useState("simple");

  const filters: PatternsFilter[] = useMemo(() => {
    const baseFilters = {
      status: status ? [{ includes: [status] }] : undefined,
      keyphrase: debouncedKeyphrase,
      deleted,
    };

    if (!filterGroups.length) {
      return [baseFilters];
    }

    return filterGroups.map((filterGroup) => {
      const titleFilters: StringFilter[] = [];
      const typeFilters: ArticleTypeFilter[] = [];
      const createdAtFilters: DateFilter[] = [];
      const updatedAtFilters: DateFilter[] = [];
      const statusFilters: ArticleStatusFilter[] = status
        ? [{ includes: [status] }]
        : [];

      filterGroup.forEach((filter) => {
        if (filter.type === "enum") {
          if (filter.configuration.name === "status") {
            statusFilters.push({
              includes:
                filter.filterType === "IS_ANY_OF"
                  ? (filter.values.map(
                      (value) => value.value,
                    ) as ArticleStatus[])
                  : undefined,
              notIncludes:
                filter.filterType === "IS_NONE_OF"
                  ? (filter.values.map(
                      (value) => value.value,
                    ) as ArticleStatus[])
                  : undefined,
            });
          }

          if (filter.configuration.name === "type") {
            typeFilters.push({
              includes:
                filter.filterType === "IS_ANY_OF"
                  ? (filter.values.map((value) => value.value) as ArticleType[])
                  : undefined,
              notIncludes:
                filter.filterType === "IS_NONE_OF"
                  ? (filter.values.map((value) => value.value) as ArticleType[])
                  : undefined,
            });
          }
        }

        if (filter.type === "string") {
          if (filter.configuration.name === "title") {
            titleFilters.push(getGraphQlStringFilter(filter));
          }
        }

        if (filter.type === "date") {
          if (filter.configuration.name === "createdAt") {
            createdAtFilters.push(getGraphQlDateFilter(filter));
          }

          if (filter.configuration.name === "updatedAt") {
            updatedAtFilters.push(getGraphQlDateFilter(filter));
          }
        }
      });

      return {
        ...baseFilters,
        status: statusFilters,
        title: titleFilters,
        type: typeFilters,
        createdAt: createdAtFilters,
        updatedAt: updatedAtFilters,
      };
    });
  }, [debouncedKeyphrase, deleted, filterGroups, status]);

  const [patternsResponse] = useQuery({
    query: PatternTableQuery,
    requestPolicy: "cache-and-network",
    variables: {
      filters,
      paginationOptions: {
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
      },
      sort: patternSort,
    },
  });

  // Whenever the filters are changed we clear the selection
  useEffect(() => {
    setRowSelection({});
  }, [filters]);

  const patterns =
    patternsResponse.data?.patterns.nodes.map((node) =>
      getFragmentData(PatternFragment, node),
    ) ?? [];

  const columnHelper = createColumnHelper<(typeof patterns)[number]>();

  const columns = [
    columnHelper.accessor("templateText", {
      header: "Pattern title",
      cell: PatternCell,
      minSize: 300,
      maxSize: 600,
      meta: {
        sticky: true,
      },
    }),
    columnHelper.accessor("articlesCount", {
      header: "Articles",
      size: 116,
      cell: ({ cell }) => new Intl.NumberFormat().format(cell.getValue()),
    }),
    columnHelper.accessor("status", {
      header: "Status",
      size: 198,
      cell: StatusCell,
    }),
    columnHelper.accessor("createdBy", {
      header: "Created by",
      size: 140,
      cell: UserCell,
    }),
    columnHelper.accessor("createdAt", {
      header: "Created",
      size: 140,
      cell: ({ cell }) => {
        const value = cell.getValue();

        return value ? <FormattedDate date={value} /> : "-";
      },
    }),
    ...(selectedTableView === "full"
      ? [
          columnHelper.accessor("updatedAt", {
            header: "Updated",
            size: 140,
            cell: ({ cell }) => {
              const value = cell.getValue();

              return value ? <FormattedDate date={value} /> : "-";
            },
          }),
        ]
      : []),
  ];

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPagination(DEFAULT_PAGINATION_STATE);
      setDebouncedKeyphrase(keyphrase);
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [keyphrase]);

  const onSortingChange = useCallback((sortState: SortingState) => {
    const sort = sortState[0];

    if (!sort) {
      setPatternSort(undefined);
      return;
    }

    const field = sort.id;
    const direction = sort.desc ? SortOrder.Desc : SortOrder.Asc;

    if (Object.values(PatternSortField).includes(field as PatternSortField)) {
      setPatternSort({
        field: field as PatternSortField,
        direction,
      });
    } else {
      setPatternSort(undefined);
    }
  }, []);

  useEffect(() => {
    if (!patternsResponse.fetching) {
      onLoad();
    }
  }, [onLoad, patternsResponse.fetching]);

  return (
    <PatternTableContext.Provider
      value={{ filters, visibleRows: patterns, setRowSelection }}
    >
      <div className={styles.tableContainer}>
        <Table
          SelectedToolbar={PatternTableToolbar}
          columns={columns}
          data={patterns}
          isSelectable
          loading={patternsResponse.fetching}
          pagination={pagination}
          setPagination={setPagination}
          totalCount={patternsResponse.data?.patterns.totalCount}
          resourceName={{ plural: "patterns", singular: "pattern" }}
          enableSorting
          onSortingChange={onSortingChange}
          isFiltered={!!filters.length}
          clearSelectionOnChange={false}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          Header={
            <div className={styles.header}>
              <TextInput
                leftSection={<Search size="medium" />}
                onChange={(event) => {
                  setKeyphrase(event.currentTarget.value);
                }}
                placeholder="Search patterns"
                value={keyphrase}
                className={styles.search}
              />
              <FilterButton
                filterCount={filterGroups.reduce(
                  (count, filter) => (count += filter.length),
                  0,
                )}
                open={open}
                clearFilters={clearFilters}
              />
              <ToggleGroup
                selectedItem={selectedTableView}
                setSelectedItem={setSelectedTableView}
                className={styles.buttonGroup}
                items={[
                  { id: "simple", name: "Simple Table" },
                  { id: "full", name: "Full Table" },
                ]}
              />
              <FilterDrawer
                filterGroups={filterGroups}
                configuration={[
                  {
                    title: "Processing",
                    configurations: [
                      {
                        name: "status",
                        label: "Status",
                        type: "enum",
                        values: Object.values(ArticleStatus).map((status) => ({
                          value: status,
                          label: status,
                        })),
                      },
                    ],
                  },
                  {
                    title: "Content",
                    configurations: [
                      { name: "title", label: "Title", type: "string" },
                      // {
                      //   name: "type",
                      //   label: "Article Type",
                      //   type: "enum",
                      //   values: Object.values(ArticleType),
                      // },
                    ],
                  },
                  {
                    title: "Dates",
                    configurations: [
                      {
                        type: "date",
                        label: "Created Date",
                        name: "createdAt",
                      },
                      {
                        type: "date",
                        label: "Updated Date",
                        name: "updatedAt",
                      },
                    ],
                  },
                ]}
                isOpen={isFiltersDrawerOpen}
                onClose={close}
                addFilter={addFilter}
                editFilter={editFilter}
                deleteFilter={deleteFilter}
              />
            </div>
          }
        />
      </div>
    </PatternTableContext.Provider>
  );
}
export default PatternTable;
